import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useCurrentSupportUser } from '@/features/dashboard-users/hooks/useCurrentSupportUser';
import { authenticate } from '@/features/2fa/store/actions/auth2Fa.actions';
import { use2FaConfig } from '@/features/2fa/hooks/use2FaConfig';
import { cancelChangePassword } from '@/features/dashboard-users/store/support-user.actions';

export default function use2FAAuthentication() {
  const dispatch = useDispatch();

  const { secureKey: passkey } = useCurrentSupportUser();
  const { scaApi } = use2FaConfig();
  const [credentials, setCredentials] = useState('');

  const getCredentials = useCallback(
    async (challenge: string) => {
      const newChallenge = JSON.stringify({
        iat: Date.now(),
        value: challenge,
      });

      const assertion = await scaApi.get(
        {
          type: 'webauthn',
          options: {
            userHandle: passkey?.authenticator?.userHandle,
            challenge: newChallenge,
            allowCredentials: [
              {
                id: passkey.authenticator?.publicKeyCredentialId,
                type: passkey.authenticator?.type,
                transports: passkey.authenticator?.transports,
              },
            ],
          },
        },
        undefined,
        async (error) => {
          console.error(error);
          dispatch(cancelChangePassword(error));
        },
      );

      return typeof assertion !== 'string' ? assertion?.webauthn : undefined;
    },
    [
      passkey.authenticator?.publicKeyCredentialId,
      passkey.authenticator?.transports,
      passkey.authenticator?.type,
      passkey.authenticator?.userHandle,
      dispatch,
      scaApi,
    ],
  );

  const authenticate2FA = useCallback(
    async (challenge: string) => {
      const credentials = await getCredentials(challenge);

      if (!!credentials) {
        dispatch(
          authenticate(credentials, (status) => {
            status === 'success' && setCredentials(credentials);
          }),
        );
      }
    },
    [getCredentials, dispatch],
  );

  return {
    authenticate2FA,
    getCredentials,
    credentials,
    setCredentials,
  };
}
